import React from 'react';
import MicroLabel from '../components/MicroLabel';
import { euro, euroPrecise } from '../format';

/**
 * Copy for each of the three ways this chapter can be sourced.
 *
 * The backend decides the source; the chapter only has to say the true thing for
 * it. "Your deposits" is only accurate when the contributions ledger is actually
 * populated — with the cost-basis fallback the figure is what the holdings were
 * bought for, which is a different claim and gets different words.
 */
const COPY = {
  ledger: {
    investedLabel: 'Your deposits',
    sentence: (perEuro) => (
      <>
        For every euro sitting in the portfolio today, {euroPrecise(perEuro)} came out
        of your pocket — the market did the rest.
      </>
    ),
  },
  'cost-basis': {
    investedLabel: 'What you paid',
    sentence: (perEuro) => (
      <>
        For every euro of assets you hold today, {euroPrecise(perEuro)} was the purchase
        price — the market added the rest.
      </>
    ),
  },
};

/**
 * Chapter 06 — What you put in. What the market added.
 *
 * Both bars are scaled against the larger of the two figures. The handoff fixes
 * the deposits bar at full width and scales gains as `gains ÷ deposits`, which
 * silently breaks for any portfolio whose gains exceed what was put in: the
 * gains bar clamps at 100% and the two read as equal. Scaling both against the
 * maximum keeps the comparison truthful and still renders the deposits bar full
 * width in the common case, exactly as drawn.
 *
 * Negative market gains collapse the gains bar to nothing; the signed figure
 * beside it carries the loss, since the design has no vocabulary for a bar
 * running the other way.
 */
function Contributions({ story }) {
  const source = story?.contributionSource;
  const invested = story?.deposits;
  const gains = story?.marketGains;
  const reference = story?.contributionReference;

  if (source === 'none') {
    return (
      <>
        <h2 className="story-h2" style={{ maxWidth: 1000 }}>
          What you put in. What the market added.
        </h2>
        <div className="story-empty">
          <p className="story-empty-text">
            Nothing to split apart yet. Once your holdings carry purchase prices — or you
            start logging deposits — this chapter shows how much of the portfolio you put
            there yourself.
          </p>
        </div>
      </>
    );
  }

  const copy = COPY[source] || COPY.ledger;

  const investedValue = Number.isFinite(invested) ? invested : 0;
  const gainsValue = Number.isFinite(gains) ? gains : 0;
  const max = Math.max(investedValue, gainsValue, 0);

  const width = (value) => (max > 0 ? `${Math.max(0, (value / max) * 100)}%` : '0%');

  const perEuro = Number.isFinite(invested) && Number.isFinite(reference) && reference > 0
    ? invested / reference
    : null;

  return (
    <>
      <h2 className="story-h2" style={{ maxWidth: 1000 }}>
        What you put in. What the market added.
      </h2>

      <div className="story-contrib">
        <div>
          <div className="story-contrib-head">
            <MicroLabel>{copy.investedLabel}</MicroLabel>
            <span className="story-contrib-figure">{euro(invested)}</span>
          </div>
          <div
            className="story-contrib-bar story-contrib-bar-deposits"
            style={{ width: width(investedValue) }}
            aria-hidden="true"
          />
        </div>

        <div>
          <div className="story-contrib-head">
            <MicroLabel tone="accent">Market gains</MicroLabel>
            <span
              className="story-contrib-figure"
              style={{ color: 'var(--color-accent-700)' }}
            >
              {euro(gains)}
            </span>
          </div>
          <div
            className="story-contrib-bar story-contrib-bar-gains"
            style={{ width: width(gainsValue) }}
            aria-hidden="true"
          />
        </div>

        <p className="story-contrib-sentence">
          {perEuro === null
            ? 'Once the portfolio has a value, this is where you see how much of it you put there yourself.'
            : copy.sentence(perEuro)}
        </p>
      </div>
    </>
  );
}

export default Contributions;
